import { memo, useEffect, useMemo } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import FormWithTabs from '@components/common/FormWithTabs';

import { useModal } from '@components/common/Modal/utils';

import { IRUConfiguration } from '@interfaces/cmModule/ruConfiguration';
import { ITabForm } from '@interfaces/components/form';

import { ERevalidationMode, EValidationMode } from '@enums/validationMode';

import { ERUConfigurationFormFields, ERUConfigurationFormTabNames } from './constants';

import RUConfigurationForm from './RUConfigurationForm/RUConfigurationForm';
import RUCarriersDataForm from './RUCarriersDataForm';
import { schema } from './schema';

interface IRUConfigurationFormProps {
	isLoading: boolean;
	editableConfiguration?: IRUConfiguration;
	configurationName: string;
	mimoMode: string;
	submitForm: (data: FieldValues) => void;
	closeModal?: () => void;
	setIsDirtyFormModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const RUConfigurationFormTabs = ({
	editableConfiguration,
	mimoMode = 'Placeholder',
	configurationName = 'Placeholder',
	isLoading,
	submitForm,
	setIsDirtyFormModal,
}: IRUConfigurationFormProps) => {
	const { onClose } = useModal();

	const {
		absoluteFrequencyCenter: nrafrcn,
		centerOfChannelBandwidth: frequency,
		channelBandwidth,
		gain,
	} = editableConfiguration?.txRxArrayCarriers?.[0] || {};

	const {
		register,
		handleSubmit,
		formState: { errors, isDirty },
		trigger,
		setValue,
		watch,
	} = useForm<FieldValues>({
		mode: EValidationMode.onChange,
		reValidateMode: ERevalidationMode.onChange,
		resolver: yupResolver(schema),
		defaultValues: {
			[ERUConfigurationFormFields.name]: editableConfiguration?.networkElementName || configurationName,
			[ERUConfigurationFormFields.mimoMode]: editableConfiguration?.mimoMode?.name || mimoMode,
			[ERUConfigurationFormFields.channelBandwidth]: channelBandwidth || '',
			[ERUConfigurationFormFields.frequency]: frequency || '',
			[ERUConfigurationFormFields.nrafrcn]: nrafrcn || '',
			[ERUConfigurationFormFields.transmissionGain]: gain || '',
			[ERUConfigurationFormFields.duMacAddress]: editableConfiguration?.duMacAddress || '',
			[ERUConfigurationFormFields.ruMacAddress]: editableConfiguration?.ruMacAddress || '',
			[ERUConfigurationFormFields.vlanId]: editableConfiguration?.cuPlaneVlanId || '',
		},
	});

	const isConfigurationFormSubmitDisabled = [
		ERUConfigurationFormFields.name,
		ERUConfigurationFormFields.vlanId,
		ERUConfigurationFormFields.ruMacAddress,
		ERUConfigurationFormFields.duMacAddress,
	].some((field) => !watch(field));

	const isCarriersDataFormSubmitDisabled = [
		ERUConfigurationFormFields.mimoMode,
		ERUConfigurationFormFields.frequency,
		ERUConfigurationFormFields.nrafrcn,
		ERUConfigurationFormFields.channelBandwidth,
		ERUConfigurationFormFields.transmissionGain,
	].some((field) => !watch(field));

	const isEditMode = !!editableConfiguration;

	const tabs: ITabForm[] = useMemo(() => ([
		{
			title: ERUConfigurationFormTabNames.configuration,
			value: ERUConfigurationFormTabNames.configuration,
			component: RUConfigurationForm,
			isTabError: !!(errors?.name
				|| errors?.vlanId
				|| errors?.ruMacAddress
				|| errors?.duMacAddress
			),
			props: {
				errors,
				isLoading,
				isSubmitDisabled: isConfigurationFormSubmitDisabled,
				isEditMode,
				onClose,
				setValue,
				register,
				trigger,
			},
		},
		{
			title: ERUConfigurationFormTabNames.carriersData,
			value: ERUConfigurationFormTabNames.carriersData,
			component: RUCarriersDataForm,
			isTabError: !!(errors?.mimoMode
				|| errors?.frequency
				|| errors?.nrafrcn
				|| errors?.channelBandwidth
				|| errors?.transmissionGain
			),
			props: {
				errors,
				isLoading,
				isSubmitDisabled: isCarriersDataFormSubmitDisabled,
				isEditMode,
				onClose,
				setValue,
				register,
				handleSubmit,
				submitForm,
			},
		},
	]), [
		errors,
		isLoading,
		isConfigurationFormSubmitDisabled,
		isCarriersDataFormSubmitDisabled,
		isEditMode,
		onClose,
		trigger,
		setValue,
		register,
		handleSubmit,
		submitForm,
	]);

	useEffect(() => setIsDirtyFormModal(isDirty), [isDirty, setIsDirtyFormModal]);

	return (
		<FormWithTabs
			isEditMode={isEditMode}
			stickyTabs
			tabs={tabs}
		/>
	);
};

export default memo(RUConfigurationFormTabs);
